import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { BULK_UPDATE_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import axios from "axios";
import { validatePhoneFilterValue } from "../../utils/phoneSearch";
import { Add } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";

const UpdateInputField = ({
  field,
  onChangeFieldType,
  onChangeFieldValue,
  onDelete,
  tagOptions,
  existingTags,
  updateInputFields,
  hasSelectedAll,
}) => {
  const selectedValues = updateInputFields
    .filter((f) => f.type === field.type)
    .map((f) => f.value);

  const addTagOptions = tagOptions;
  const removeTagOptions = hasSelectedAll ? tagOptions : existingTags;

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <TextField
        label="Type"
        value={field.type || ""}
        onChange={onChangeFieldType}
        select
        sx={{ flex: 1 }}
      >
        <MenuItem value="tag-add">Tag (Add)</MenuItem>
        <MenuItem value="tag-remove">Tag (Remove)</MenuItem>
      </TextField>

      <TextField
        select
        label="Value"
        value={field.value || ""}
        onChange={onChangeFieldValue}
        sx={{ flex: 1 }}
      >
        {field.type === "tag-add"
          ? addTagOptions.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                disabled={
                  selectedValues.includes(option.id) &&
                  field.value !== option.id
                }
              >
                {option.name}
              </MenuItem>
            ))
          : field.type === "tag-remove" &&
            removeTagOptions.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                disabled={
                  selectedValues.includes(option.id) &&
                  field.value !== option.id
                }
              >
                {option.name}
              </MenuItem>
            ))}
      </TextField>

      <IconButton variant="contained" onClick={onDelete}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const BulkUpdateModal = ({
  open,
  handleClose,
  tagOptions,
  bulkUpdateData,
  setCreateTagModalOpen,
  hasSelectedAll = false,
  activeFilters = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updateInputFields, setUpdateInputFields] = useState([
    { type: "tag-add", value: "" },
  ]);

  const existingTags = bulkUpdateData
    .map((data) => data.tags)
    .flat()
    .filter(
      (obj, index, self) => index === self.findIndex((t) => t.id === obj.id)
    );

  const { getValidToken } = useAuth();

  const convertFiltersToQueryParams = (filters) => {
    if (!filters || filters.length === 0) return (new URLSearchParams({})).toString();

    let queryParams = new URLSearchParams({
      limit: 1,
      offset: 0,
    });

    filters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      let v = value ?? "";
      if (field === "phone" && v !== "") {
        const check = validatePhoneFilterValue(String(v));
        v = check.valid ? check.normalizedForApi : v;
      }
      queryParams.append(field, `${operator}:${v}`);
    });

    return queryParams.toString();
  };

  const handleCancel = () => {
    setError(null);
    setUpdateInputFields([{ type: "tag-add", value: "" }]);
    handleClose();
  };

  const bulkUpdate = async () => {
    setLoading(true);
    setError(null);

    try {
      // Collect audience IDs
      const audienceIds = bulkUpdateData.map((aud) => aud.id);

      // Collect tag actions
      const addTags = updateInputFields
        .filter((field) => field.type === "tag-add" && field.value)
        .map((field) => field.value);

      const removeTags = updateInputFields
        .filter((field) => field.type === "tag-remove" && field.value)
        .map((field) => field.value);

      const payload = {
        audience_ids: audienceIds,
        tag_add_ids: addTags,
        tag_remove_ids: removeTags,
        all: hasSelectedAll,
        active_filters: hasSelectedAll ? convertFiltersToQueryParams(activeFilters) : "",
      };

      const apiUrl = BULK_UPDATE_ENDPOINT;

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error({ message: "Error: " + response.data.error });
      }

      handleCancel();
    } catch (err) {
      if (err.response && err.response.data) {
        const data = err.response.data;

        // Case 1: API returned {"error": "..."}
        if (data.error) {
          setError(data.error);
        }
        // Case 2: DRF validation errors like {"tags": ["..."], "phone_no": ["..."]}
        else if (typeof data === "object") {
          const messages = [];
          for (const key in data) {
            if (Array.isArray(data[key])) {
              messages.push(`${key}: ${data[key].join(", ")}`);
            } else {
              messages.push(`${key}: ${data[key]}`);
            }
          }
          setError(messages.join(" | "));
        } else {
          setError("Failed to bulk update.");
        }
      } else if (err.message) {
        setError("Failed to bulk update: " + err.message);
      } else {
        setError("Failed to bulk update.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1202 }}
    >
      <DialogTitle>Bulk Update</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} p={2}>
          {error && (
            <Alert severity="error">{typeof error === "string" && error}</Alert>
          )}

          {updateInputFields.map((field, index) => (
            <UpdateInputField
              tagOptions={tagOptions}
              existingTags={existingTags}
              field={field}
              updateInputFields={updateInputFields}
              hasSelectedAll={hasSelectedAll}
              onChangeFieldType={(e) => {
                const updatedFields = [...updateInputFields];
                updatedFields[index] = {
                  ...updatedFields[index],
                  type: e.target.value,
                  value: "",
                };
                setUpdateInputFields(updatedFields);
              }}
              onChangeFieldValue={(e) => {
                const updatedFields = [...updateInputFields];
                updatedFields[index] = {
                  ...updatedFields[index],
                  value: e.target.value,
                };
                setUpdateInputFields(updatedFields);
              }}
              onDelete={() => {
                let updatedFields = [...updateInputFields];
                updatedFields = updatedFields.filter((_, i) => i !== index);
                setUpdateInputFields(updatedFields);
              }}
            />
          ))}

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={() => {
                if (updateInputFields.length < 5) {
                  setUpdateInputFields((prev) => [
                    ...prev,
                    { type: "tag-add", value: "" },
                  ]);
                }
              }}
              disabled={updateInputFields.length >= 5}
              sx={{
                width: "fit-content",
              }}
            >
              Add
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={() => setCreateTagModalOpen(true)}
              sx={{
                width: "fit-content",
              }}
            >
              Create Tag
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={bulkUpdate}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Bulk Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateModal;
